import { motion, AnimatePresence } from "framer-motion";
import { X, ClockCounterClockwise, Trash, ArrowClockwise, SealCheck, Warning } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";

export function HistoryDrawer({ open, onClose, items, onLoad, onDelete }) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[65]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="history-drawer">
          <div className="absolute inset-0 bg-lc-navy/50 backdrop-blur-sm" onClick={onClose} />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-lc-ink/10 bg-white"
          >
            <div className="flex items-center justify-between border-b border-lc-ink/10 px-5 py-4">
              <h2 className="flex items-center gap-2 font-serif text-xl text-lc-ink">
                <ClockCounterClockwise size={20} weight="duotone" className="text-lc-ocean" /> {t("ws.history")}
              </h2>
              <button onClick={onClose} data-testid="history-close" className="text-lc-steel hover:text-lc-ink transition-colors duration-200">
                <X size={18} />
              </button>
            </div>
            <div className="lc-scroll flex-1 overflow-y-auto p-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center py-20 text-center" data-testid="history-empty">
                  <ClockCounterClockwise size={40} weight="duotone" className="text-lc-ink/20" />
                  <p className="mt-4 text-sm text-lc-steel">{t("md.histEmpty1")}<br />{t("md.histEmpty2")}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {items.map((it) => {
                    const ready = it.result?.presentation_ready;
                    return (
                      <div key={it.id} data-testid={`history-item-${it.id}`} className="rounded-sm border border-lc-ink/10 p-3.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              {ready ? <SealCheck size={15} weight="fill" className="text-lc-ok" /> : <Warning size={15} weight="fill" className="text-lc-critical" />}
                              <span className="font-mono text-[11px] text-lc-steel">{new Date(it.created_at).toLocaleString()}</span>
                            </div>
                            <p className="mt-1 truncate text-xs text-lc-ink">
                              {t("md.histScore").replace("{score}", it.result?.readiness_score ?? "–").replace("{crit}", it.result?.counts?.critical ?? 0).replace("{docs}", it.documents?.length ?? 0)}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <button onClick={() => onLoad(it)} data-testid={`history-load-${it.id}`}
                              className="rounded-sm border border-lc-ink/15 p-1.5 text-lc-ocean hover:bg-lc-paper transition-colors duration-200" title={t("md.reload")}>
                              <ArrowClockwise size={14} />
                            </button>
                            <button onClick={() => onDelete(it.id)} data-testid={`history-delete-${it.id}`}
                              className="rounded-sm border border-lc-ink/15 p-1.5 text-lc-steel hover:text-lc-critical transition-colors duration-200" title={t("md.delete")}>
                              <Trash size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
